/**
 * Config Store
 *
 * Zustand store for config state:
 * - Current config path
 * - Config metadata
 * - Loading state
 */

import { create } from "zustand";

import { configApi } from "../api/configApi";
import type { ConfigMetadata, ConfigVersionEntry } from "../types";

import { useAuthStore } from "./authStore";
import { useEditorStore } from "./editorStore";

interface ConfigState {
  // Current config
  readonly currentPath: string | undefined;
  readonly metadata: ConfigMetadata | undefined;

  // Loading state
  readonly isLoading: boolean;
  readonly error: string | undefined;

  // History
  readonly versionHistory: readonly ConfigVersionEntry[];
  readonly isLoadingHistory: boolean;

  // Actions
  readonly setCurrentPath: (path: string | undefined) => void;
  readonly setMetadata: (metadata: ConfigMetadata | undefined) => void;
  readonly loadConfig: (path: string) => Promise<void>;
  readonly loadHistory: () => Promise<void>;
  readonly refreshConfigs: () => Promise<void>;
  readonly reset: () => void;
}

const initialState = {
  currentPath: undefined,
  metadata: undefined,
  isLoading: false,
  error: undefined,
  versionHistory: [],
  isLoadingHistory: false,
};

export const useConfigStore = create<ConfigState>()((set, get) => ({
  ...initialState,

  setCurrentPath: (path: string | undefined) => {
    set({ currentPath: path });
    if (path) {
      get().loadConfig(path);
    } else {
      set({ metadata: undefined });
      useEditorStore.getState().reset();
    }
  },

  setMetadata: (metadata: ConfigMetadata | undefined) => {
    set({ metadata });
  },

  loadConfig: async (path: string) => {
    const { tenantId, environment } = useAuthStore.getState();

    if (!tenantId) {
      set({ error: "Not authenticated" });
      return;
    }

    set({ isLoading: true, error: undefined });

    try {
      const result = await configApi.getConfig({
        tenantId,
        environment,
        path,
      });

      if (!result.success) {
        throw new Error(result.error.message);
      }

      const { content, metadata } = result.data;

      set({
        metadata,
        isLoading: false,
      });

      // Set editor content
      useEditorStore.getState().setOriginalContent(content);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load config";
      set({ error: message, isLoading: false });
    }
  },

  loadHistory: async () => {
    const { currentPath, metadata } = get();
    const { tenantId, environment } = useAuthStore.getState();

    if (!tenantId || !currentPath || !metadata) {
      return;
    }

    set({ isLoadingHistory: true });

    try {
      const result = await configApi.getConfigHistory({
        tenantId,
        environment,
        path: currentPath,
      });

      if (!result.success) {
        throw new Error(result.error.message);
      }

      set({ versionHistory: result.data, isLoadingHistory: false });
    } catch {
      set({ versionHistory: [], isLoadingHistory: false });
    }
  },

  refreshConfigs: async () => {
    // This triggers a refetch in react-query via the query key
    // The actual refresh is handled by the FileTree component
    set({ isLoading: true });

    // Simulate refresh delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    set({ isLoading: false });
  },

  reset: () => {
    set(initialState);
    useEditorStore.getState().reset();
  },
}));

// When current path changes, load the config
useConfigStore.subscribe((state, prevState) => {
  if (state.currentPath !== prevState.currentPath && state.currentPath) {
    state.loadConfig(state.currentPath);
  }
});
